import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Filter } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const propertyTypes = ['Alle', 'Wohnung', 'Haus', 'Gewerbe', 'Grundstück'];
const transactionTypes = ['Alle', 'Kaufen', 'Mieten'];

export default function Immobilien() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    propertyType: 'Alle',
    transactionType: 'Alle'
  });

useEffect(() => {
    const loadProperties = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.propertyType !== 'Alle') params.append('property_type', filters.propertyType);
        if (filters.transactionType !== 'Alle') params.append('transaction_type', filters.transactionType);
        const response = await axios.get(`${API}/api/properties?${params.toString()}`);
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProperties();
  }, [filters]);

  return (
    <div data-testid="immobilien-page">
      {/* Hero */}
      <section className="relative pt-32 pb-12 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Immobilien</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              AKTUELLE ANGEBOTE
            </span>
            <h1 className="font-display text-4xl sm:text-5xl text-white mt-4 mb-6">
              Immobilien im Rhein-Main-Gebiet
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-[#d1af6a]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-[#c8c6c4]">
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter:</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <Select
                value={filters.propertyType}
                onValueChange={(value) => setFilters(prev => ({ ...prev, propertyType: value }))}
              >
                <SelectTrigger className="w-[180px] input-luxury" data-testid="filter-property-type">
                  <SelectValue placeholder="Objektart" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.transactionType}
                onValueChange={(value) => setFilters(prev => ({ ...prev, transactionType: value }))}
              >
                <SelectTrigger className="w-[180px] input-luxury" data-testid="filter-transaction-type">
                  <SelectValue placeholder="Kaufen/Mieten" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  {transactionTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#c8c6c4] text-lg">
                Keine Immobilien gefunden.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a2640]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">
            Nicht das Richtige gefunden?
          </h2>
          <p className="text-[#c8c6c4] mb-8">
            Kontaktieren Sie mich für eine individuelle Immobiliensuche.
          </p>
          <Link to="/kontakt" className="btn-primary">
            Suchauftrag stellen
          </Link>
        </div>
      </section>
    </div>
  );
}
